import { useState } from "react";

import { ChevronRight } from "lucide-react";

import { Avatar } from "@components/ui/avatar";

import { useMe } from "@hooks/queries/use-me";
import { useUserProfile } from "@hooks/useUserProfile";

import { cn } from "@utils/ui";

import { useSidebar } from "../store";
import { UserCommandPopover } from "./UserCommandPopover";

export const UserProfileSection = () => {
  const { isExpanded } = useSidebar();

  const userProfile = useUserProfile();
  const { data: me } = useMe();
  const userType = me?.user?.user_type;
  const isProvider = userType === "provider";
  const isPortal = isProvider || userType === "referrer";

  const [popoverOpen, setPopoverOpen] = useState(false);

  const name = userProfile?.name || "";
  const email = userProfile?.email || "";
  const avatar = undefined; // TODO: update avatar; No avatar URL available from MSAL

  // External portals (referrer + provider): navy footer matching the design —
  // gradient avatar + name + a role subtitle, borderless with a subtle top
  // divider. The display name prefers the DB record (`/me`) since external
  // (email-code) users have no MSAL profile name client-side.
  if (isPortal) {
    const portalName =
      me?.user?.name || name || email || (isProvider ? "Provider" : "Referrer");
    const portalInitials =
      portalName
        .split(/\s+/)
        .filter(Boolean)
        .map((w) => w[0])
        .join("")
        .slice(0, 2)
        .toUpperCase() || (isProvider ? "P" : "R");
    const portalSubtitle = isProvider
      ? "SureMed Records · ConnectNet"
      : email || "Referrer Portal";
    return (
      <UserCommandPopover open={popoverOpen} onOpenChange={setPopoverOpen}>
        <div
          className={cn(
            "flex w-full min-w-0 cursor-pointer items-center gap-[11px] overflow-hidden border-t border-white/[0.07]",
            isExpanded ? "px-4 py-3.5" : "justify-center p-2.5"
          )}
        >
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[9px] bg-gradient-to-br from-[#0065CB] to-[#00D8D8] text-[13px] font-bold text-white">
            {portalInitials}
          </div>
          {isExpanded && (
            <div className="min-w-0 flex-1 overflow-hidden">
              <div className="truncate font-head text-[13px] font-semibold leading-[1.2] text-white">
                {portalName}
              </div>
              <div className="truncate text-[11px] text-[#7e8ba0]">
                {portalSubtitle}
              </div>
            </div>
          )}
        </div>
      </UserCommandPopover>
    );
  }

  // Flush row on the navy chrome — no card/border, just a subtle hover fill —
  // matching the portal branch above and the design's profile row (Claude
  // Design "Report Studio UI Update" / Main Pages (Half-Dark).dc.html).
  return (
    <UserCommandPopover open={popoverOpen} onOpenChange={setPopoverOpen}>
      <div
        className={cn(
          "flex min-w-0 cursor-pointer items-center gap-[11px] overflow-hidden border-t border-border transition-colors duration-150 hover:bg-primary/[0.07] dark:border-white/[0.08] dark:hover:bg-white/[0.06]",
          isExpanded ? "px-2.5 py-3" : "justify-center p-2.5"
        )}
      >
        <Avatar
          size="sm"
          src={avatar}
          alt={name}
          fallback={name
            .split(" ")
            .map((n) => n[0])
            .join("")
            .toUpperCase()}
          className="size-[30px] flex-shrink-0"
          fallbackClassName="bg-dark-50 text-foreground-muted font-bold text-[10.5px] dark:bg-white/[0.14] dark:text-foreground-light"
        />
        {isExpanded && (
          <>
            <div className="min-w-0 flex-1 leading-tight">
              <div className="overflow-hidden text-ellipsis whitespace-nowrap font-head text-[12.5px] font-semibold text-foreground dark:text-foreground-light">
                {name}
              </div>
              <div className="overflow-hidden text-ellipsis whitespace-nowrap text-[11px] text-foreground-muted dark:text-foreground-light/55">
                {email}
              </div>
            </div>
            <ChevronRight
              className="flex-shrink-0 text-foreground-light-muted dark:text-foreground-light/50"
              size={15}
            />
          </>
        )}
      </div>
    </UserCommandPopover>
  );
};
